// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import CaretRight from "@pacific/XDL/Icons/CaretRight";
import PlayCircle from "@pacific/XDL/Icons/PlayCircle";
import ShoppingBag from "@pacific/XDL/Icons/ShoppingBag";
import { headingsFontConfig } from "@pacific/XDL/PreStyle/typography";
import { color, mergeWithCss, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    buttonText: string;
    title: string;
    description?: string;
    buttonUrl: LinkFieldValue;
    imageUrl: string;
    icon: string;
    openInNewTab?: boolean;
    iconSize: number;
    extendedStyles?: InformationCardStyles;
    linkElementId?: string;
    gaEvent?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    session: getCustomTypeSession(state),
    currentBillTo: getCurrentBillToState(state).value,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const enum icons {
    caretRight = "CaretRight",
    shoppingBag = "ShoppingBag",
    playCircle = "PlayCircle",
}

export interface InformationCardStyles {
    wrapper?: InjectableCss;
    topSection?: InjectableCss;
    bottomSection?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    iconWrapper?: InjectableCss;
}

export const informationCardStyles: InformationCardStyles = {
    wrapper: {
        css: css`
            border: 1px solid ${color("common.border")};
            background-color: ${color("common.ui1")};
            display: flex;
            flex-direction: column;
            min-height: 100%;
            width: 100%;
        `,
    },
    topSection: {
        css: css`
            padding: 16px 16px 22px;
            flex-grow: 1;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            height: 154px;
            margin-bottom: 16px;

            ${xScreen("lte", "md")} {
                height: 125px;
            }

            ${xScreen("lte", "sm")} {
                height: auto;
            }
        `,
    },
    image: {
        height: "154px",
        transformations: {
            width: 384,
            tabletWidth: 447,
            mobileWidth: 702,
        },
        css: css`
            width: 100%;
            max-height: 100%;
        `,
    },
    title: {
        variant: "h4",
        css: css`
            margin-bottom: 16px;
        `,
    },
    description: {
        variant: "p",
        css: css`
            &,
            ${TypographyStyle} {
                margin-bottom: 0;
            }
        `,
    },
    bottomSection: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            background-color: ${color("common.ui2")};
            display: flex;
            justify-content: flex-end;
            min-height: 41px;
        `,
    },
    link: {
        typographyProps: {
            variant: "h6",
            forwardAs: "span",
        },
        css: css`
            margin-right: 8px;
            letter-spacing: 0;
            &,
            ${TypographyStyle} {
                line-height: 16px;
                margin-bottom: 0;
                font-size: 14px;
            }
        `,
    },
    iconWrapper: {
        css: css`
            height: 100%;
            min-height: 41px;
            width: 41px;
            display: flex;
            justify-content: center;
            align-items: center;
            border-left: 1px solid ${color("common.border")};
            background-color: ${color("common.ui1")};
        `,
    },
};

const InfoCard: React.FC<Props> = ({
    buttonText,
    title,
    description,
    buttonUrl,
    imageUrl,
    icon,
    openInNewTab = false,
    iconSize = 14,
    linkElementId,
    gaEvent,
    currentBillTo,
    isAuthenticated,
    session,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(informationCardStyles, extendedStyles));
    const GetIcon = (icon: string) => {
        switch (icon) {
            case icons.caretRight:
                return CaretRight;
            case icons.shoppingBag:
                return ShoppingBag;
            case icons.playCircle:
                return PlayCircle;
            default:
                return CaretRight;
        }
    };

    const link = useGetLink(buttonUrl);

    const _GaInfoCardEvents = (event?: string, title?: string, url?: string) => {
        const dataLayer = getDataLayer();
        if (!dataLayer || !event) {
            return;
        }
        const googleAnalyticsData = {
            event,
            click_text: title,
            destination: url,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.topSection}>
                <StyledWrapper {...styles.imageWrapper}>
                    <LazyImage src={imageUrl} {...styles.image} altText={title} />
                </StyledWrapper>
                <Typography {...styles.title}>{title}</Typography>
                {description && <Typography {...styles.description}>{parse(description, parserOptions)}</Typography>}
            </StyledWrapper>
            <StyledWrapper {...styles.bottomSection}>
                <Link
                    href={link.url}
                    target={openInNewTab ? "_blank" : ""}
                    {...styles.link}
                    aria-label={buttonText}
                    id={linkElementId}
                    onClick={() => _GaInfoCardEvents(gaEvent, buttonText, link.url)}
                >
                    {buttonText}
                </Link>
                <StyledWrapper {...styles.iconWrapper}>
                    <Icon src={GetIcon(icon)} size={iconSize} color="primary.main" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(InfoCard);
